/**
 * The street-cleaning day, drawn the same way wherever a customer is listed.
 *
 * The schedule is free and belongs to the person, not to a plan, so it shows
 * up on Customers as well as Subscribers — and both tabs have to agree, or an
 * operator reading one and acting on the other will move a car on the wrong
 * day. Hence one file.
 *
 * Every date here is New York's. The operator's laptop may be anywhere; the
 * sweep is not.
 */
import React from 'react';
import { Box } from '@mui/material';
import { VALET, FONTS } from '../../theme';

const NY = 'America/New_York';

/** Today in New York as 'YYYY-MM-DD'. en-CA is the locale that formats that way. */
export function nyTodayKey() {
    try {
        return new Intl.DateTimeFormat('en-CA', {
            timeZone: NY,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).format(new Date());
    } catch (e) {
        return new Date().toISOString().slice(0, 10);
    }
}

function addDays(dateKey, n) {
    // Noon keeps the date from rolling either way on a daylight-saving day.
    const d = new Date(`${dateKey}T12:00:00Z`);
    return new Date(d.getTime() + n * 86400000).toISOString().slice(0, 10);
}

/** 'Today' / 'Tomorrow' / 'Tue Sep 1' — how a person says a date out loud. */
export function dayKeyLabel(dateKey, todayKey) {
    if (!dateKey) return '';
    const today = todayKey || nyTodayKey();
    if (dateKey === today) return 'Today';
    if (dateKey === addDays(today, 1)) return 'Tomorrow';
    if (dateKey === addDays(today, -1)) return 'Yesterday';
    return new Date(`${dateKey}T12:00:00Z`).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
    });
}

/** The clock time of an occurrence, read in New York. */
export function moveTimeLabel(occurrence) {
    if (!occurrence) return '';
    if (occurrence.timeLabel) return occurrence.timeLabel;
    const d = new Date(occurrence.at);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: NY,
    });
}

/**
 * The second line under the day: what happens next, and whether anything is
 * standing in the way of it. Returns null when there is nothing to say.
 */
export function nextMoveLine(cleaning, todayKey) {
    if (!cleaning || !cleaning.hasSchedule) return null;

    if (!cleaning.active) {
        const until = cleaning.pausedUntil
            ? ` until ${dayKeyLabel(new Date(cleaning.pausedUntil).toISOString().slice(0, 10), todayKey)}`
            : '';
        // A paused schedule books nothing at all — worth saying, not colouring quietly.
        return { text: `Paused${until} — no moves`, tone: VALET.warning };
    }

    const firstSuspended = (cleaning.upcoming || []).find((u) => u.suspended);
    if (!cleaning.next) {
        return firstSuspended
            ? { text: `${dayKeyLabel(firstSuspended.dateKey, todayKey)} suspended`, tone: VALET.textMuted }
            : null;
    }

    const when = dayKeyLabel(cleaning.next.dateKey, todayKey);
    const time = moveTimeLabel(cleaning.next);
    const soon = when === 'Today' || when === 'Tomorrow';
    // 'Today 9:00 AM' reads as an instruction; 'Next Tue, Sep 1 · 9:00 AM'
    // reads as a diary entry. Which one it is depends on how close it is.
    const line = {
        text: soon ? `${when}${time ? ` ${time}` : ''}` : `Next ${when}${time ? ` · ${time}` : ''}`,
        tone: soon ? VALET.success : VALET.textDim,
    };

    // The city cancelled a sweep between now and then, so the next move is not
    // the one the customer is expecting.
    if (firstSuspended && firstSuspended.dateKey < cleaning.next.dateKey) {
        line.text = `${dayKeyLabel(firstSuspended.dateKey, todayKey)} suspended · next ${when}`;
        line.tone = VALET.textMuted;
    }
    return line;
}

/**
 * The table cell: the days on top, what happens next underneath.
 * `todayKey` comes from the API when it is there, so the console and the
 * scheduler agree about what day it is.
 */
export function CleaningCell({ cleaning, todayKey }) {
    if (!cleaning || !cleaning.hasSchedule) {
        return <Box sx={{ color: VALET.textMuted, fontFamily: FONTS.sans, fontSize: '12.5px' }}>Not set</Box>;
    }
    const sub = nextMoveLine(cleaning, todayKey);
    return (
        <Box>
            <Box sx={{
                fontFamily: FONTS.mono,
                fontSize: '12.5px',
                color: cleaning.active ? VALET.textPrimary : VALET.textMuted,
                whiteSpace: 'nowrap',
            }}>
                {cleaning.shortLabel}
            </Box>
            {sub && (
                <Box sx={{
                    fontFamily: FONTS.sans,
                    fontSize: '11px',
                    color: sub.tone,
                    marginTop: '2px',
                    whiteSpace: 'nowrap',
                }}>
                    {sub.text}
                </Box>
            )}
        </Box>
    );
}

/**
 * The colour that one-line version should be: amber for a paused schedule,
 * green when the car has to move today or tomorrow. On a phone the whole line
 * is one colour, and that is the thing worth scanning for.
 */
export function cleaningChipTone(cleaning, todayKey) {
    const sub = nextMoveLine(cleaning, todayKey);
    return (sub && sub.tone) || VALET.textDim;
}

/** One-line version for the phone cards, where there is no room for two. */
export function cleaningChipText(cleaning, todayKey) {
    if (!cleaning || !cleaning.hasSchedule) return null;
    const sub = nextMoveLine(cleaning, todayKey);
    // An em dash, not another '·' — a two-day schedule already uses those to
    // separate its own days, and three dots in a row read as one long list.
    return sub ? `${cleaning.shortLabel} — ${sub.text}` : cleaning.shortLabel;
}
